"use client";

import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useRef, useState } from "react";
import { isPermissionDeniedError } from "@/lib/native/permission-guidance";

export type VoiceRecorderState =
  | "idle"
  | "recording"
  | "locked"
  | "paused"
  | "preview";

export interface VoiceRecordingResult {
  file: File;
  durationMs: number;
  /** Normalized 0-100 peaks, ≤64 buckets (schema allows up to 256). */
  waveform: number[];
  previewUrl: string;
}

export type VoiceRecorderStartResult =
  | { ok: true }
  | {
      ok: false;
      reason: "busy" | "failed" | "permission_denied" | "unsupported";
      message?: string;
    };

export interface UseVoiceRecorderOptions {
  /**
   * A take was kept (released, stopped, interrupted) but was too short to be
   * a voice note, so it was dropped. Surface it — silently snapping back to
   * idle reads as "the mic is broken".
   */
  onTooShort?: () => void;
}

export interface UseVoiceRecorderResult {
  state: VoiceRecorderState;
  /** Live state for async callers (a `.then` that outlived its render). */
  getState: () => VoiceRecorderState;
  /** Rolling live levels (0-100) while recording, newest last. */
  levels: number[];
  elapsedMs: number;
  result: VoiceRecordingResult | null;
  start: () => Promise<VoiceRecorderStartResult>;
  /** Stop and keep the take (state → preview). */
  stop: () => void;
  /** Stop and discard everything (state → idle). */
  cancel: () => void;
  /** Hands-free mode — keeps recording without holding the mic. */
  lock: () => void;
  /** Pause a hands-free recording without discarding the take. */
  pause: () => void;
  /** Resume a paused hands-free recording. */
  resume: () => void;
  /** Discard a finished take from the preview state. */
  discard: () => void;
}

export const MAX_VOICE_NOTE_MS = 120_000;
const LEVEL_SAMPLE_MS = 80;
const LIVE_LEVEL_WINDOW = 48;
const WAVEFORM_BUCKETS = 64;
export const MIN_VOICE_NOTE_MS = 400;

/** WebKit records AAC-in-mp4; Chrome records opus-in-webm. Both are accepted
 * by the voice-note upload slot. */
function pickSupportedMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null;
  const candidates = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"];
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return null;
}

function extensionForMime(mimeType: string) {
  if (mimeType.startsWith("audio/mp4")) return "m4a";
  if (mimeType.includes("wav")) return "wav";
  return "webm";
}

function audioBufferToWavBlob(buffer: AudioBuffer): Blob {
  const sampleRate = buffer.sampleRate;
  const sampleCount = buffer.length;
  const channelCount = 1;
  const bytesPerSample = 2;
  const blockAlign = channelCount * bytesPerSample;
  const dataSize = sampleCount * blockAlign;
  const output = new ArrayBuffer(44 + dataSize);
  const view = new DataView(output);

  const writeString = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  const channels = Array.from({ length: buffer.numberOfChannels }, (_, index) =>
    buffer.getChannelData(index),
  );
  let offset = 44;
  for (let sampleIndex = 0; sampleIndex < sampleCount; sampleIndex += 1) {
    let mixed = 0;
    for (const channel of channels) {
      mixed += channel[sampleIndex] ?? 0;
    }
    mixed /= Math.max(1, channels.length);
    const clamped = Math.max(-1, Math.min(1, mixed));
    view.setInt16(
      offset,
      clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff,
      true,
    );
    offset += 2;
  }

  return new Blob([output], { type: "audio/wav" });
}

async function convertToCrossPlatformAudioBlob(
  blob: Blob,
  mimeType: string,
): Promise<{ blob: Blob; mimeType: string }> {
  const baseMime = mimeType.split(";")[0] || blob.type || "audio/webm";
  if (
    baseMime === "audio/mp4" ||
    baseMime === "audio/m4a" ||
    baseMime === "audio/wav" ||
    baseMime === "audio/wave" ||
    baseMime === "audio/x-wav"
  ) {
    return { blob, mimeType: baseMime };
  }

  const AudioContextCtor =
    window.AudioContext ??
    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    return { blob, mimeType: baseMime };
  }

  const audioContext = new AudioContextCtor();
  try {
    const decoded = await audioContext.decodeAudioData(
      await blob.arrayBuffer(),
    );
    return { blob: audioBufferToWavBlob(decoded), mimeType: "audio/wav" };
  } finally {
    void audioContext.close().catch(() => {});
  }
}

function downsampleWaveform(samples: number[], buckets: number): number[] {
  if (samples.length === 0) return [];
  const size = Math.min(buckets, samples.length);
  const result: number[] = [];
  for (let i = 0; i < size; i++) {
    const startIdx = Math.floor((i * samples.length) / size);
    const endIdx = Math.max(
      startIdx + 1,
      Math.floor(((i + 1) * samples.length) / size),
    );
    let peak = 0;
    for (let j = startIdx; j < endIdx; j++) {
      if (samples[j] > peak) peak = samples[j];
    }
    result.push(Math.round(Math.min(100, Math.max(0, peak))));
  }
  return result;
}

export function useVoiceRecorder({
  onTooShort,
}: UseVoiceRecorderOptions = {}): UseVoiceRecorderResult {
  const [state, setState] = useState<VoiceRecorderState>("idle");
  const [levels, setLevels] = useState<number[]>([]);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [result, setResult] = useState<VoiceRecordingResult | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const allSamplesRef = useRef<number[]>([]);
  const startedAtRef = useRef(0);
  const elapsedBeforePauseRef = useRef(0);
  const tickTimerRef = useRef<number | null>(null);
  const intentRef = useRef<"keep" | "discard">("keep");
  const stateRef = useRef<VoiceRecorderState>("idle");
  const resultRef = useRef<VoiceRecordingResult | null>(null);
  const onTooShortRef = useRef(onTooShort);
  onTooShortRef.current = onTooShort;

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const getState = useCallback(() => stateRef.current, []);

  const teardownAudioGraph = useCallback(() => {
    if (tickTimerRef.current) {
      window.clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
    for (const track of streamRef.current?.getTracks() ?? []) {
      track.stop();
    }
    streamRef.current = null;
    void audioContextRef.current?.close().catch(() => {});
    audioContextRef.current = null;
    mediaRecorderRef.current = null;
  }, []);

  const finalizeTake = useCallback(async (mimeType: string) => {
    const activeElapsed =
      stateRef.current === "paused"
        ? 0
        : Math.max(0, performance.now() - startedAtRef.current);
    const durationMs = Math.min(
      Math.round(elapsedBeforePauseRef.current + activeElapsed),
      MAX_VOICE_NOTE_MS,
    );
    const baseMime = mimeType.split(";")[0] || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: baseMime });
    chunksRef.current = [];

    if (
      intentRef.current === "discard" ||
      blob.size === 0 ||
      durationMs < MIN_VOICE_NOTE_MS
    ) {
      const wasKept = intentRef.current === "keep";
      stateRef.current = "idle";
      setState("idle");
      setLevels([]);
      setElapsedMs(0);
      if (wasKept) onTooShortRef.current?.();
      return;
    }

    let playableBlob = blob;
    let playableMimeType = baseMime;
    try {
      const converted = await convertToCrossPlatformAudioBlob(blob, baseMime);
      playableBlob = converted.blob;
      playableMimeType = converted.mimeType;
    } catch {
      playableBlob = blob;
      playableMimeType = baseMime;
    }

    const file = new File(
      [playableBlob],
      `voice-${Date.now()}.${extensionForMime(playableMimeType)}`,
      { type: playableMimeType },
    );
    const take: VoiceRecordingResult = {
      file,
      durationMs,
      waveform: downsampleWaveform(allSamplesRef.current, WAVEFORM_BUCKETS),
      previewUrl: URL.createObjectURL(playableBlob),
    };
    resultRef.current = take;
    setResult(take);
    stateRef.current = "preview";
    setState("preview");
  }, []);

  const stopRecorder = useCallback(
    (intent: "keep" | "discard") => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") return;
      intentRef.current = intent;
      try {
        recorder.stop();
      } catch {
        teardownAudioGraph();
        stateRef.current = "idle";
        setState("idle");
      }
    },
    [teardownAudioGraph],
  );

  const start = useCallback(async (): Promise<VoiceRecorderStartResult> => {
    if (
      stateRef.current === "recording" ||
      stateRef.current === "locked" ||
      stateRef.current === "paused"
    ) {
      return { ok: false, reason: "busy" };
    }

    const mimeType = pickSupportedMimeType();
    if (
      !mimeType ||
      typeof navigator === "undefined" ||
      !navigator.mediaDevices
    ) {
      if (Capacitor.isNativePlatform()) {
        return {
          ok: false,
          reason: "permission_denied",
          message:
            "Microphone recording is unavailable until microphone access is enabled for Hiive.",
        };
      }

      return { ok: false, reason: "unsupported" };
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (error) {
      return {
        ok: false,
        reason: isPermissionDeniedError(error) ? "permission_denied" : "failed",
        message: error instanceof Error ? error.message : undefined,
      };
    }

    // Pause any playing chat audio — WKWebView's audio session can't record
    // cleanly while <audio> playback is active.
    for (const audio of Array.from(document.querySelectorAll("audio"))) {
      audio.pause();
    }

    streamRef.current = stream;
    chunksRef.current = [];
    allSamplesRef.current = [];
    intentRef.current = "keep";
    startedAtRef.current = performance.now();
    elapsedBeforePauseRef.current = 0;
    setLevels([]);
    setElapsedMs(0);
    setResult(null);
    resultRef.current = null;

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType });
    } catch (error) {
      teardownAudioGraph();
      return {
        ok: false,
        reason: "failed",
        message: error instanceof Error ? error.message : undefined,
      };
    }
    mediaRecorderRef.current = recorder;
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => {
      teardownAudioGraph();
      void finalizeTake(mimeType);
    };
    // A recorder error (encoder failure, device lost) keeps what we have.
    recorder.onerror = () => stopRecorder("keep");
    recorder.start(500);

    // The OS can end the mic track under us — an incoming call, another app
    // taking the audio session, AirPods disconnecting. MediaRecorder then
    // records silence (WebKit) or stalls; stop cleanly and keep the take.
    for (const track of stream.getAudioTracks()) {
      track.addEventListener(
        "ended",
        () => {
          if (mediaRecorderRef.current !== recorder) return;
          stopRecorder("keep");
        },
        { once: true },
      );
    }

    const AudioContextCtor =
      window.AudioContext ??
      (window as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (AudioContextCtor) {
      const audioContext = new AudioContextCtor();
      audioContextRef.current = audioContext;
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const buffer = new Uint8Array(analyser.fftSize);

      tickTimerRef.current = window.setInterval(() => {
        if (stateRef.current === "paused") {
          setElapsedMs(Math.round(elapsedBeforePauseRef.current));
          return;
        }
        analyser.getByteTimeDomainData(buffer);
        let sumSquares = 0;
        for (const value of buffer) {
          const centered = (value - 128) / 128;
          sumSquares += centered * centered;
        }
        const rms = Math.sqrt(sumSquares / buffer.length);
        // RMS of speech rarely exceeds ~0.5; scale so voices fill the bar.
        const level = Math.min(100, Math.round(rms * 260));
        allSamplesRef.current.push(level);
        setLevels((previous) => {
          const next = [...previous, level];
          return next.length > LIVE_LEVEL_WINDOW
            ? next.slice(next.length - LIVE_LEVEL_WINDOW)
            : next;
        });

        const elapsed =
          elapsedBeforePauseRef.current +
          performance.now() -
          startedAtRef.current;
        setElapsedMs(Math.round(elapsed));
        if (elapsed >= MAX_VOICE_NOTE_MS) {
          stopRecorder("keep");
        }
      }, LEVEL_SAMPLE_MS);
    } else {
      tickTimerRef.current = window.setInterval(() => {
        if (stateRef.current === "paused") {
          setElapsedMs(Math.round(elapsedBeforePauseRef.current));
          return;
        }
        const elapsed =
          elapsedBeforePauseRef.current +
          performance.now() -
          startedAtRef.current;
        setElapsedMs(Math.round(elapsed));
        if (elapsed >= MAX_VOICE_NOTE_MS) {
          stopRecorder("keep");
        }
      }, LEVEL_SAMPLE_MS);
    }

    stateRef.current = "recording";
    setState("recording");
    return { ok: true };
  }, [finalizeTake, stopRecorder, teardownAudioGraph]);

  const stop = useCallback(() => stopRecorder("keep"), [stopRecorder]);
  const cancel = useCallback(() => stopRecorder("discard"), [stopRecorder]);

  const lock = useCallback(() => {
    if (stateRef.current === "recording") {
      stateRef.current = "locked";
      setState("locked");
    }
  }, []);

  const pause = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (
      recorder?.state !== "recording" ||
      (stateRef.current !== "recording" && stateRef.current !== "locked")
    ) {
      return;
    }
    try {
      recorder.pause();
      elapsedBeforePauseRef.current += Math.max(
        0,
        performance.now() - startedAtRef.current,
      );
      setElapsedMs(Math.round(elapsedBeforePauseRef.current));
      stateRef.current = "paused";
      setState("paused");
    } catch {}
  }, []);

  const resume = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder?.state !== "paused") return;
    try {
      recorder.resume();
      startedAtRef.current = performance.now();
      stateRef.current = "locked";
      setState("locked");
    } catch {}
  }, []);

  const discard = useCallback(() => {
    if (resultRef.current) {
      URL.revokeObjectURL(resultRef.current.previewUrl);
      resultRef.current = null;
    }
    setResult(null);
    setLevels([]);
    setElapsedMs(0);
    stateRef.current = "idle";
    setState("idle");
  }, []);

  // Interruptions (backgrounding, call, tab hide) stop-and-keep the take.
  useEffect(() => {
    const handleHidden = () => {
      if (document.visibilityState !== "hidden") return;
      if (
        stateRef.current === "recording" ||
        stateRef.current === "locked" ||
        stateRef.current === "paused"
      ) {
        stopRecorder("keep");
      }
    };
    document.addEventListener("visibilitychange", handleHidden);
    return () => document.removeEventListener("visibilitychange", handleHidden);
  }, [stopRecorder]);

  // Unmount: discard everything. This also runs when Next 16 hides the route
  // inside an <Activity> — effects clean up but state SURVIVES, so the state
  // must be reset too, or the composer comes back showing a preview whose
  // blob URL was just revoked (and a "recording" with no recorder behind it).
  // On a real unmount the setState calls are no-ops.
  useEffect(() => {
    return () => {
      intentRef.current = "discard";
      const recorder = mediaRecorderRef.current;
      if (recorder) {
        recorder.onstop = null;
        recorder.onerror = null;
        try {
          if (recorder.state !== "inactive") recorder.stop();
        } catch {}
      }
      mediaRecorderRef.current = null;
      if (tickTimerRef.current) window.clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
      for (const track of streamRef.current?.getTracks() ?? []) {
        track.stop();
      }
      streamRef.current = null;
      void audioContextRef.current?.close().catch(() => {});
      audioContextRef.current = null;
      chunksRef.current = [];
      if (resultRef.current) URL.revokeObjectURL(resultRef.current.previewUrl);
      resultRef.current = null;
      stateRef.current = "idle";
      setState("idle");
      setResult(null);
      setLevels([]);
      setElapsedMs(0);
    };
  }, []);

  return {
    state,
    getState,
    levels,
    elapsedMs,
    result,
    start,
    stop,
    cancel,
    lock,
    pause,
    resume,
    discard,
  };
}
